<template>
  <el-form-item
    :class="{ desktop: isDesktop }"
    label="Flat deal statistics"
    label-position="top"
  >
    <LoadingBlock :loading="loading" />
    <template v-if="showStatistic && !loading">
      <el-collapse>
        <el-collapse-item
          v-if="mapChartData.length"
          title="Top Geo"
          name="geodata"
        >
          <MapChart :data="mapChartData" />
        </el-collapse-item>
      </el-collapse>
      <el-collapse>
        <el-collapse-item
          v-if="pieChartData.length"
          title="Devices"
          name="device"
        >
          <PieChart :data="pieChartData" />
        </el-collapse-item>
      </el-collapse>
      <el-collapse>
        <el-collapse-item
          v-if="barChartData.length"
          title="Browsers"
          name="browsers"
        >
          <BarChart :data="barChartData" />
        </el-collapse-item>
      </el-collapse>
    </template>
    <template v-if="!showStatistic && !loading">
      No statistics available for this deal right now
    </template>
  </el-form-item>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import MapChart from '@/apps/traforama/components/MapChart.vue'
import PieChart from '@/apps/traforama/components/PieChart.vue'
import BarChart from '@/apps/traforama/components/BarChart.vue'
import LoadingBlock from '@/components/LoadingBlock.vue'
import useResize from '@/hooks/useResize'

import { useFlatDealsStore } from '@/apps/traforama/store/flatDeals'

interface IGroupedData {
  [key: string]: number
}
export interface ISpotEntity {
  name: string
  value: number
}
export interface IChartEntity {
  title: string
  value: number
  id: string
}
interface ISpotData {
  [key: string]: Array<ISpotEntity>
}

const ISOs: Record<string, string> = {
  Switzerland: 'ch',
  Iceland: 'is',
  Kuwait: 'kw',
  Russia: 'ru',
  Singapore: 'sg',
  'South Africa': 'za',
  Norway: 'no',
  'Bouvet Island': 'bv',
  'Saint Martin': 'mf',
  Kosovo: 'xk',
  'Cocos [Keeling] Islands': 'cc',
  'Guinea-Bissau': 'gw',
  'South Georgia and the South Sandwich Islands': 'gs',
  Israel: 'il',
  Japan: 'jp',
  Nigeria: 'ng',
  'Hong Kong': 'hk',
  Ghana: 'gh',
  'El Salvador': 'sv',
  'Heard Island and McDonald Islands': 'hm',
  'South Sudan': 'ss',
  Jersey: 'je',
  Guernsey: 'gg',
  'U.S. Minor Outlying Islands': 'um',
  'Saint Helena': 'sh',
  'Isle of Man': 'im',
  Serbia: 'rs',
  Curaçao: 'cw',
  'Saint Pierre and Miquelon': 'pm',
  'Puerto Rico': 'pr',
  'Sint Maarten': 'sx',
  Germany: 'de',
  Australia: 'au',
  'Svalbard and Jan Mayen': 'sj',
  Congo: 'cd',
  Unknown: 'un',
  'United Arab Emirates': 'ae',
  'Saint-Barthélemy': 'bl',
  Bonaire: 'bq',
  'North Korea': 'kp',
  'British Virgin Islands': 'vg',
  Canada: 'ca',
  'United States': 'us',
  Denmark: 'dk',
  Sweden: 'se',
  France: 'fr',
  Netherlands: 'nl',
  Spain: 'es',
  'United Kingdom': 'gb',
  Poland: 'pl',
  Hungary: 'hu',
  Estonia: 'ee',
  Cyprus: 'cy',
  'Czech Republic': 'cz',
  Ukraine: 'ua',
  Thailand: 'th',
  Bahrain: 'bh',
  Mauritius: 'mu',
  Vietnam: 'vn',
  Peru: 'pe',
  Indonesia: 'id',
  Ecuador: 'ec',
  China: 'cn',
  Turkey: 'tr',
  'Saudi Arabia': 'sa',
  Kenya: 'ke',
  Morocco: 'ma',
  Iraq: 'iq',
  Lebanon: 'lb',
  Tunisia: 'tn',
  Paraguay: 'py',
  Uruguay: 'uy',
  Bahamas: 'bs',
  Honduras: 'hn',
  Nicaragua: 'ni',
  'Costa Rica': 'cr',
  Panama: 'pa',
  Chile: 'cl',
  'Sri Lanka': 'lk',
  Mexico: 'mx',
  Argentina: 'ar',
  Colombia: 'co',
  'Dominican Republic': 'do',
  Jamaica: 'jm',
  Maldives: 'mv',
  Brazil: 'br',
  Bangladesh: 'bd',
  Greece: 'gr',
  'Hashemite Kingdom of Jordan': 'jo',
  'Norfolk Island': 'nf',
  Greenland: 'gl',
  Taiwan: 'tw',
  Venezuela: 've',
  Malta: 'mt',
  Bulgaria: 'bg',
  Romania: 'ro',
  Latvia: 'lv',
  Slovenia: 'si',
  'Slovak Republic': 'sk',
  Lithuania: 'lt',
  Palau: 'pw',
  Somalia: 'so',
  Uganda: 'ug',
  Gibraltar: 'gi',
  Oman: 'om',
  Eritrea: 'er',
  Sudan: 'sd',
  Burundi: 'bi',
  Belarus: 'by',
  Malawi: 'mw',
  Botswana: 'bw',
  Mayotte: 'yt',
  Turkmenistan: 'tm',
  Tuvalu: 'tv',
  Nauru: 'nr',
  Vanuatu: 'vu',
  Senegal: 'sn',
  Liberia: 'lr',
  'Burkina Faso': 'bf',
  Togo: 'tg',
  Benin: 'bj',
  'Sierra Leone': 'sl',
  Gambia: 'gm',
  Guinea: 'gn',
  Niger: 'ne',
  Mali: 'ml',
  Grenada: 'gd',
  'Western Sahara': 'eh',
  Azerbaijan: 'az',
  Albania: 'al',
  Libya: 'ly',
  Armenia: 'am',
  Georgia: 'ge',
  Mongolia: 'mn',
  Zambia: 'zm',
  Ethiopia: 'et',
  Syria: 'sy',
  Austria: 'at',
  Belgium: 'be',
  Finland: 'fi',
  Ireland: 'ie',
  Italy: 'it',
  Luxembourg: 'lu',
  'New Zealand': 'nz',
  Portugal: 'pt',
  Cambodia: 'kh',
  'Trinidad and Tobago': 'tt',
  Zimbabwe: 'zw',
  Mauritania: 'mr',
  Djibouti: 'dj',
  Mozambique: 'mz',
  Rwanda: 'rw',
  Fiji: 'fj',
  Qatar: 'qa',
  'Central African Republic': 'cf',
  Seychelles: 'sc',
  Yemen: 'ye',
  'Solomon Islands': 'sb',
  Gabon: 'ga',
  'Marshall Islands': 'mh',
  Madagascar: 'mg',
  Chad: 'td',
  Comoros: 'km',
  'Christmas Island': 'cx',
  Brunei: 'bn',
  'Cook Islands': 'ck',
  'New Caledonia': 'nc',
  Swaziland: 'sz',
  Niue: 'nu',
  'Saint Kitts and Nevis': 'kn',
  Macao: 'mo',
  'San Marino': 'sm',
  Antarctica: 'aq',
  'Pitcairn Islands': 'pn',
  Samoa: 'ws',
  Åland: 'ax',
  Montenegro: 'me',
  Angola: 'ao',
  Barbados: 'bb',
  Guyana: 'gy',
  Suriname: 'sr',
  Dominica: 'dm',
  Belize: 'bz',
  Guatemala: 'gt',
  Haiti: 'ht',
  Kiribati: 'ki',
  Tonga: 'to',
  Lesotho: 'ls',
  Réunion: 're',
  Bhutan: 'bt',
  Nepal: 'np',
  India: 'in',
  Tajikistan: 'tj',
  'Bosnia and Herzegovina': 'ba',
  Kazakhstan: 'kz',
  'Faroe Islands': 'fo',
  Cuba: 'cu',
  'Saint Lucia': 'lc',
  Uzbekistan: 'uz',
  'Antigua and Barbuda': 'ag',
  'Saint Vincent and the Grenadines': 'vc',
  'Equatorial Guinea': 'gq',
  'British Indian Ocean Territory': 'io',
  Namibia: 'na',
  Algeria: 'dz',
  Bermuda: 'bm',
  Aruba: 'aw',
  Anguilla: 'ai',
  'American Samoa': 'as',
  'Republic of the Congo': 'cg',
  Martinique: 'mq',
  'French Polynesia': 'pf',
  'French Southern Territories': 'tf',
  Guadeloupe: 'gp',
  Guam: 'gu',
  'Cayman Islands': 'ky',
  'Northern Mariana Islands': 'mp',
  'Turks and Caicos Islands': 'tc',
  'Vatican City': 'va',
  'Wallis and Futuna': 'wf',
  Montserrat: 'ms',
  Kyrgyzstan: 'kg',
  'Cape Verde': 'cv',
  Tanzania: 'tz',
  'Myanmar [Burma]': 'mm',
  'Papua New Guinea': 'pg',
  'Falkland Islands': 'fk',
  Tokelau: 'tk',
  Andorra: 'ad',
  Liechtenstein: 'li',
  Monaco: 'mc',
  Laos: 'la',
  'Ivory Coast': 'ci',
  'São Tomé and Príncipe': 'st',
  'East Timor': 'tl',
  Croatia: 'hr',
  Bolivia: 'bo',
  Iran: 'ir',
  'Federated States of Micronesia': 'fm',
  'French Guiana': 'gf',
  'Republic of Moldova': 'md',
  Macedonia: 'mk',
  Palestine: 'ps',
  'U.S. Virgin Islands': 'vi',
  'Republic of Korea': 'kr',
  Afghanistan: 'af',
  Pakistan: 'pk',
  Malaysia: 'my',
  Philippines: 'ph',
  Egypt: 'eg',
  Cameroon: 'cm'
}

export default defineComponent({
  name: 'StatsInfo',
  components: {
    MapChart,
    PieChart,
    LoadingBlock,
    BarChart
  },
  props: {
    id: {
      type: [Number, String],
      required: true
    }
  },
  setup(props, context) {
    const flatDealsStore = useFlatDealsStore()
    const { isDesktop } = useResize()

    const data = ref<any[]>([])
    const mapChartData = ref<IChartEntity[]>([])
    const pieChartData = ref<IChartEntity[]>([])
    const barChartData = ref<IChartEntity[]>([])
    const showStatistic = ref(false)
    const loading = ref(true)

    flatDealsStore
      .FetchStats(Number(props.id))
      .then((res) => {
        if (!res) {
          showStatistic.value = false
          return
        }
        data.value = res

        mapChartData.value = groupStats('countries')
        pieChartData.value = groupStats('devices')
        barChartData.value = groupStats('browsers')
        showStatistic.value =
          !!mapChartData.value.length ||
          !!pieChartData.value.length ||
          !!barChartData.value.length
      })
      .finally(() => {
        loading.value = false
      })

    const groupStats = (key: string) => {
      const groupedData: IGroupedData = data.value.reduce(
        (grouped: IGroupedData, spot: ISpotData) => {
          if (!spot[key]) return grouped

          spot[key].forEach((item: any) => {
            const name = item.name
            if (!grouped[name]) grouped[name] = 0
            grouped[name] += item.value
          })

          return grouped
        },
        {}
      )

      return Object.entries(groupedData)
        .sort(([, a], [, b]) => b - a)
        .map(([k, v]) => {
          const groupName =
            key === 'countries' ? (ISOs[k] || 'un').toUpperCase() : k
          return {
            id: groupName,
            value: v,
            title: k
          }
        })
    }

    return {
      mapChartData,
      pieChartData,
      barChartData,
      showStatistic,
      isDesktop,
      loading
    }
  }
})
</script>

<style lang="scss" scoped>
.el-collapse {
  width: 100%;
}
.logo-container {
  height: 70%;
}

.logo {
  max-width: -webkit-fill-available;
  max-height: 100%;
}

.flex-container {
  display: flex;
  justify-content: space-between;

  &.center {
    justify-content: center;
  }

  &.around {
    justify-content: space-around;
  }
}

.footer {
  padding: 10px 5px 0 5px;
  border-top: 1px solid #e6ebf5;
}

.body {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.el-form {
  margin: 0 20px;
}

.el-card {
  padding-bottom: 5px;
  margin-top: 15px;
  &:hover {
    cursor: pointer;
  }
  &.disabled {
    opacity: 0.4;
  }
}

:deep(.el-alert) {
  margin-bottom: 10px;
}
</style>
